// app/context/BusinessContext.js
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const ENABLE_API_SYNC = true; // Enable sync for subscription (others are handled by screens)

// Helper to generate company code
const generateCompanyCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Initial state - Enhanced to match your existing screens
const initialState = {
  user: {
    id: null,
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    profile_image: null,
    job_title: '',
    department: '',
    employee_id: '',
    start_date: '',
    manager: '',
    skills: [],
    certifications: [],
    experience_years: 0,
    company_name: '',
    company_id: '',
    office_location: '',
    team: '',
    access_level: '',
    preferences: {
      notifications: {
        email: true,
        push: true,
        sms: false,
        marketing: false,
        security_alerts: true,
        job_updates: true,
        team_updates: true,
      },
      privacy: {
        profile_visibility: 'team',
        contact_visibility: 'managers',
        activity_visibility: 'private',
        search_visibility: true,
        data_sharing: false,
      },
      security: {
        two_factor_enabled: false,
        biometric_enabled: false,
        session_timeout: 30,
        login_alerts: true,
        device_tracking: true,
      },
    },
  },
  companyProfile: {
    id: null,
    name: '',
    email: '',
    industry: '',
    companySize: '',
    isMultiUser: false,
    adminEmail: '',
    teamMembers: [],
    companyCode: '',
    subscriptionPlan: '',
    logo: null,
    headquarters: '',
    description: '',
    founded: new Date().getFullYear(),
    totalEmployees: 0,
    activeProjects: 0,
    annualRevenue: '',
    website: '',
    phone: '',
    linkedIn: '',
    twitter: '',
    facebook: '',
    companyType: '',
    vatNumber: '',
    registrationNumber: '',
    invoicePrefix: 'INV',
    defaultTaxRate: '20',
    paymentTerms: '30',
    invoiceFooter: 'Thank you for your business. Payment is due within 30 days.',
    invoiceNotes: 'All charges are in GBP. Please quote invoice number with payment.',
    bankName: '',
    bankAccount: '',
    bankSortCode: '',
    bankIBAN: '',
    bankSWIFT: '',
    specializations: [],
    locations: [],
    certifications: [],
    awards: [],
    statistics: {
      projectsCompleted: 0,
      clientRetentionRate: 0,
      onTimeDelivery: 0,
      safetyRecord: 0,
      customerSatisfaction: 0,
      teamGrowth: 0,
      budgetAccuracy: 0,
      qualityCertifications: 0,
    },
  },
  teamMembers: [],
  businessStats: {
    activeJobs: 0,
    applications: 0,
    interviews: 0,
    offers: 0,
    projects: 0,
    teamMembers: 0,
    revenue: 0,
    clients: 0,
    vendors: 0,
    contracts: 0,
    invoices: 0,
    campaigns: 0,
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalClients: 0,
    activeClients: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    profitMargin: 0,
    clientSatisfaction: 0,
    employeeRetention: 0,
    marketShare: 0,
    growthRate: 0,
  },
  notifications: [],
  subscription: null,  // null until loaded from storage/DB
  locations: [],
  awards: [],
  isLoading: false,
  isRefreshing: false,
  isOnline: true,
  apiAvailable: false,
  syncStatus: 'idle',
  error: null,
  activeTab: 'overview',
  showNotifications: false,
  showQuickActions: false,
};

// Action types
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_REFRESHING: 'SET_REFRESHING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_ONLINE_STATUS: 'SET_ONLINE_STATUS',
  SET_API_AVAILABLE: 'SET_API_AVAILABLE',
  SET_SYNC_STATUS: 'SET_SYNC_STATUS',
  SET_USER: 'SET_USER',
  UPDATE_USER: 'UPDATE_USER',
  SET_COMPANY_PROFILE: 'SET_COMPANY_PROFILE',
  UPDATE_COMPANY_PROFILE: 'UPDATE_COMPANY_PROFILE',
  SET_TEAM_MEMBERS: 'SET_TEAM_MEMBERS',
  ADD_TEAM_MEMBER: 'ADD_TEAM_MEMBER',
  UPDATE_TEAM_MEMBER: 'UPDATE_TEAM_MEMBER',
  REMOVE_TEAM_MEMBER: 'REMOVE_TEAM_MEMBER',
  SET_BUSINESS_STATS: 'SET_BUSINESS_STATS',
  UPDATE_BUSINESS_STATS: 'UPDATE_BUSINESS_STATS',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  MARK_NOTIFICATION_READ: 'MARK_NOTIFICATION_READ',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
  SET_SUBSCRIPTION: 'SET_SUBSCRIPTION',
  UPDATE_SUBSCRIPTION: 'UPDATE_SUBSCRIPTION',
  SET_LOCATIONS: 'SET_LOCATIONS',
  ADD_LOCATION: 'ADD_LOCATION',
  REMOVE_LOCATION: 'REMOVE_LOCATION',
  SET_AWARDS: 'SET_AWARDS',
  ADD_AWARD: 'ADD_AWARD',
  REMOVE_AWARD: 'REMOVE_AWARD',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_SHOW_NOTIFICATIONS: 'SET_SHOW_NOTIFICATIONS',
  SET_SHOW_QUICK_ACTIONS: 'SET_SHOW_QUICK_ACTIONS',
  CLEAR_ALL_DATA: 'CLEAR_ALL_DATA',
};

// Reducer
const businessReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case actionTypes.SET_REFRESHING:
      return { ...state, isRefreshing: action.payload };
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    case actionTypes.CLEAR_ERROR:
      return { ...state, error: null };
    case actionTypes.SET_ONLINE_STATUS:
      return { ...state, isOnline: action.payload };
    case actionTypes.SET_API_AVAILABLE:
      return { ...state, apiAvailable: action.payload };
    case actionTypes.SET_SYNC_STATUS:
      return { ...state, syncStatus: action.payload };
    case actionTypes.SET_USER:
      return { ...state, user: action.payload };
    case actionTypes.UPDATE_USER:
      return { ...state, user: { ...state.user, ...action.payload } };
    case actionTypes.SET_COMPANY_PROFILE:
      return { ...state, companyProfile: action.payload };
    case actionTypes.UPDATE_COMPANY_PROFILE:
      return { ...state, companyProfile: { ...state.companyProfile, ...action.payload } };
    case actionTypes.SET_TEAM_MEMBERS:
      return { ...state, teamMembers: action.payload };
    case actionTypes.ADD_TEAM_MEMBER:
      return { ...state, teamMembers: [...state.teamMembers, action.payload] };
    case actionTypes.UPDATE_TEAM_MEMBER:
      return {
        ...state,
        teamMembers: state.teamMembers.map(member =>
          member.id === action.payload.id ? { ...member, ...action.payload } : member
        ),
      };
    case actionTypes.REMOVE_TEAM_MEMBER:
      return {
        ...state,
        teamMembers: state.teamMembers.filter(m => m.id !== action.payload),
      };
    case actionTypes.SET_BUSINESS_STATS:
      return { ...state, businessStats: action.payload };
    case actionTypes.UPDATE_BUSINESS_STATS:
      return { ...state, businessStats: { ...state.businessStats, ...action.payload } };
    case actionTypes.SET_NOTIFICATIONS:
      return { ...state, notifications: action.payload };
    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 50),
      };
    case actionTypes.MARK_NOTIFICATION_READ:
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: true } : n
        ),
      };
    case actionTypes.CLEAR_NOTIFICATIONS:
      return { ...state, notifications: [] };
    case actionTypes.SET_SUBSCRIPTION:
      return { ...state, subscription: action.payload };
    case actionTypes.UPDATE_SUBSCRIPTION:
      return { ...state, subscription: { ...state.subscription, ...action.payload } };
    case actionTypes.SET_LOCATIONS:
      return { ...state, locations: action.payload };
    case actionTypes.ADD_LOCATION:
      return { ...state, locations: [...state.locations, action.payload] };
    case actionTypes.REMOVE_LOCATION:
      return {
        ...state,
        locations: state.locations.filter((_, i) => i !== action.payload),
      };
    case actionTypes.SET_AWARDS:
      return { ...state, awards: action.payload };
    case actionTypes.ADD_AWARD:
      return { ...state, awards: [...state.awards, action.payload] };
    case actionTypes.REMOVE_AWARD:
      return {
        ...state,
        awards: state.awards.filter((_, i) => i !== action.payload),
      };
    case actionTypes.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload };
    case actionTypes.SET_SHOW_NOTIFICATIONS:
      return { ...state, showNotifications: action.payload };
    case actionTypes.SET_SHOW_QUICK_ACTIONS:
      return { ...state, showQuickActions: action.payload };
    case actionTypes.CLEAR_ALL_DATA:
      return { ...initialState };
    default:
      return state;
  }
};

// Helper to reliably get business ID
// NOTE: company_profile is the CANONICAL source of the active company (this is
// what boot()/the screens trust). We read it FIRST so a stale `company_id` key
// left over from a previous session can't return the wrong company — that was
// the root of the company_id:1 cross-company sync bug.
const getBusinessId = async () => {
  try {
    let id = null;
    const profile = await AsyncStorage.getItem('company_profile');
    if (profile) {
      try { id = JSON.parse(profile)?.id ?? null; } catch (_) {}
    }
    if (!id) id = await AsyncStorage.getItem('company_id');
    return id ? parseInt(id, 10) : null;
  } catch (error) {
    console.error('Error getting businessId:', error);
    return null;
  }
};

// Create context
const BusinessContext = createContext(null);

// Provider component
export const BusinessProvider = ({ children }) => {
  const [state, dispatch] = useReducer(businessReducer, initialState);

  // ==================== CONNECTIVITY ====================
  const checkConnectivity = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'HEAD',
        headers: { 'X-API-Key': API_KEY },
        signal: controller.signal,
      }).catch(() => null);
      clearTimeout(timeoutId);
      const online = response?.ok || false;
      dispatch({ type: actionTypes.SET_ONLINE_STATUS, payload: online });
      dispatch({ type: actionTypes.SET_API_AVAILABLE, payload: online });
      return online;
    } catch {
      dispatch({ type: actionTypes.SET_ONLINE_STATUS, payload: false });
      dispatch({ type: actionTypes.SET_API_AVAILABLE, payload: false });
      return false;
    }
  }, []);

  // ==================== API CALLS (with API key) ====================
  const apiCall = useCallback(async (endpoint, options = {}) => {
    if (!state.apiAvailable && !options.force) {
      throw new Error('API not available');
    }

    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...options.headers,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || 30000);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('API call error:', err);
      throw err;
    }
  }, [state.apiAvailable]);

  // ==================== LOAD ALL DATA ====================
  // Fetch fresh company profile from API and sync to context + AsyncStorage
  const fetchAndSyncCompanyProfile = useCallback(async () => {
    try {
      console.log('[Context] fetchAndSyncCompanyProfile starting...');
      const token = await AsyncStorage.getItem('auth_token')
                  || await AsyncStorage.getItem('token');
      if (!token) { console.log('[Context] No auth token — skipping company sync'); return; }

      let cid = await AsyncStorage.getItem('company_id')
              || await AsyncStorage.getItem('company_id');
      if (!cid) {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) { try { cid = JSON.parse(cp)?.id; } catch {} }
      }
      if (!cid) { console.log('[Context] No company ID — skipping company sync'); return; }
      console.log('[Context] Syncing company profile for cid:', cid);

      const res = await fetch(`${API_BASE_URL}/api/company-profile/${cid}`, {
        headers: {
          'X-API-Key':    API_KEY,
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      const raw  = data.data || data;
      if (!raw?.id) return;

      const profile = {
        id:                 String(raw.id),
        name:               raw.company_name || raw.name || '',
        email:              raw.email || '',
        phone:              raw.phone || '',
        website:            raw.website || '',
        industry:           raw.industry || '',
        companySize:        raw.employee_count ? String(raw.employee_count) : '',
        logo:               raw.logo || raw.logo_url || raw.banner_image_url || null,
        headquarters:       raw.address || '',
        description:        raw.description || '',
        vatNumber:          raw.vat_number || '',
        registrationNumber: raw.registration_number || '',
        companyCode:        raw.company_code || '',
        companyType:        raw.company_type || '',
        founded:            raw.founded ? String(raw.founded) : '',
        linkedIn:           raw.linkedin || raw.linkedin_url || '',
      };

      await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
      dispatch({ type: actionTypes.SET_COMPANY_PROFILE, payload: profile });
      console.log('[Context] Company profile synced on login:', profile.name);
    } catch (e) {
      console.warn('[Context] fetchAndSyncCompanyProfile:', e.message);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });

      const userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        const mergedUser = {
          ...initialState.user,
          ...parsedUser,
          preferences: {
            ...initialState.user.preferences,
            ...(parsedUser.preferences || {}),
          },
        };
        dispatch({ type: actionTypes.SET_USER, payload: mergedUser });
      }

      // Step 1: Load cached company profile instantly
      const companyData = await AsyncStorage.getItem('company_profile');
      if (companyData) {
        const parsedCompany = JSON.parse(companyData);
        dispatch({ type: actionTypes.SET_COMPANY_PROFILE, payload: parsedCompany });
        if (parsedCompany.locations) {
          dispatch({ type: actionTypes.SET_LOCATIONS, payload: parsedCompany.locations });
        }
        if (parsedCompany.awards) {
          dispatch({ type: actionTypes.SET_AWARDS, payload: parsedCompany.awards });
        }
      }

      // Step 2: Fetch fresh from API in background (non-blocking)
      fetchAndSyncCompanyProfile().catch(() => {});

      const teamData = await AsyncStorage.getItem('team_members');
      if (teamData) {
        dispatch({ type: actionTypes.SET_TEAM_MEMBERS, payload: JSON.parse(teamData) });
      }

      const subData = await AsyncStorage.getItem('business_subscription');
      if (subData) {
        const parsed = JSON.parse(subData);
        // Normalise
        parsed.price     = parseFloat(parsed.price || parsed.price_paid || 0);
        parsed.plan_name = parsed.plan_name || parsed.name || parsed.plan_id || 'Business Plan';
        // Fix status — if price > 0 and not explicitly cancelled, mark active
        if (!parsed.status || parsed.status === 'inactive') {
          parsed.status = parsed.price > 0 ? 'active' : 'inactive';
        }
        dispatch({ type: actionTypes.SET_SUBSCRIPTION, payload: parsed });
      }

      const statsData = await AsyncStorage.getItem('business_stats');
      if (statsData) {
        dispatch({ type: actionTypes.SET_BUSINESS_STATS, payload: JSON.parse(statsData) });
      }

      const notifData = await AsyncStorage.getItem('notifications');
      if (notifData) {
        dispatch({ type: actionTypes.SET_NOTIFICATIONS, payload: JSON.parse(notifData) });
      }

    } catch (error) {
      console.error('Error loading business data:', error);
      dispatch({ type: actionTypes.SET_ERROR, payload: 'Failed to load business data' });
    } finally {
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
    }
  }, []);

  // ==================== SAVE ALL DATA ====================
  const saveAllData = useCallback(async () => {
    try {
      const dataToSave = {
        user: state.user,
        companyProfile: state.companyProfile,
        subscription: state.subscription,
        teamMembers: state.teamMembers,
        businessStats: state.businessStats,
        notifications: state.notifications,
        lastUpdated: new Date().toISOString(),
      };
      await AsyncStorage.setItem('business_data_backup', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving business data:', error);
    }
  }, [state]);

  // ==================== NOTIFICATION ACTIONS ====================
  const addNotification = useCallback((notification) => {
    dispatch({ type: actionTypes.ADD_NOTIFICATION, payload: notification });
  }, []);

  const markNotificationRead = useCallback((notificationId) => {
    dispatch({ type: actionTypes.MARK_NOTIFICATION_READ, payload: notificationId });
  }, []);

  const clearNotifications = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_NOTIFICATIONS });
  }, []);

  // ==================== ENSURE USER HAS COMPANY ====================
  const ensureUserHasCompany = useCallback(async (userId) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (!token || !userId) return null;

      // Check if user already has a company
      const response = await fetch(`${API_BASE_URL}/api/company-profile?user_id=${userId}`, {
        headers: {
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });
      
      const result = await response.json();
      
      if (result.success && result.data && result.data.length > 0) {
        // User already has a company
        const company = result.data[0];
        const profile = {
          id: company.id,
          name: company.company_name || '',
          email: company.email || '',
          industry: company.industry || '',
          companySize: company.employee_count ? String(company.employee_count) : '',
          logo: company.logo || null,
          companyCode: company.company_code || '',
          user_id: company.user_id,
        };
        
        await AsyncStorage.setItem('company_id', company.id);
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
        dispatch({ type: actionTypes.SET_COMPANY_PROFILE, payload: profile });
        
        return profile;
      }

      // No company found, create one
      const userEmail = await AsyncStorage.getItem('user_email');
      const userName = await AsyncStorage.getItem('user_name');
      
      const newCompanyData = {
        company_name: userName ? `${userName.split(' ')[0]}'s Company` : 'My Company',
        email: userEmail || '',
        industry: '',
        employee_count: 1,
        phone: '',
        website: '',
        logo: null,
        address: '',
        description: '',
        vat_number: '',
        registration_number: '',
        company_code: generateCompanyCode(),
        user_id: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const createResponse = await fetch(`${API_BASE_URL}/api/company-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newCompanyData),
      });

      const createResult = await createResponse.json();
      
      if (createResult.success && createResult.data) {
        const company = createResult.data;
        const profile = {
          id: company.id,
          name: company.company_name || '',
          email: company.email || '',
          industry: company.industry || '',
          companySize: company.employee_count ? String(company.employee_count) : '',
          logo: company.logo || null,
          companyCode: company.company_code || '',
          user_id: company.user_id,
        };
        
        await AsyncStorage.setItem('company_id', company.id);
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
        dispatch({ type: actionTypes.SET_COMPANY_PROFILE, payload: profile });
        
        return profile;
      }
      
      return null;
    } catch (error) {
      console.error('Error ensuring user has company:', error);
      return null;
    }
  }, []);

  // ==================== SYNC FUNCTIONS ====================
  const syncInProgress = React.useRef(false);
  const syncCompanyToAPI = useCallback(async (profileData) => {
    if (!ENABLE_API_SYNC) return false;
    if (!state.apiAvailable) return false;
    if (syncInProgress.current) { console.log('[Context] Sync already in progress, skipping'); return false; }
    syncInProgress.current = true;

    try {
      dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'syncing' });
      const token = await AsyncStorage.getItem('auth_token');
      const businessId = await getBusinessId();

      const dbData = {
        company_name:        profileData.name,
        email:               profileData.email,
        industry:            profileData.industry,
        employee_count:      profileData.companySize ? parseInt(profileData.companySize) || null : null,
        phone:               profileData.phone,
        website:             profileData.website,
        // Skip large base64 logos in sync — they're already saved via direct PUT
        logo:                profileData.logo && profileData.logo.startsWith('data:') ? undefined : profileData.logo,
        address:             profileData.headquarters || profileData.address,
        headquarters:        profileData.headquarters,
        description:         profileData.description,
        vat_number:          profileData.vatNumber,
        registration_number: profileData.registrationNumber,
        company_code:        profileData.companyCode,
        company_type:        profileData.companyType || null,
        founded_date:        profileData.founded     || null,
        linkedin:            profileData.linkedIn    || profileData.linkedin_url || null,
        twitter:             profileData.twitterUrl  || null,
        facebook:            profileData.facebookUrl || null,
        bank_name:           profileData.bankName    || null,
        bank_account:        profileData.bankAccount || null,
        bank_sort_code:      profileData.bankSortCode|| null,
        bank_iban:           profileData.bankIBAN    || null,
        bank_swift:          profileData.bankSWIFT   || null,
        invoice_prefix:      profileData.invoicePrefix    || null,
        default_tax_rate:    profileData.defaultTaxRate
                             ? parseFloat(profileData.defaultTaxRate) : null,
        payment_terms:       profileData.paymentTerms
                             ? parseInt(profileData.paymentTerms) : null,
      };

      Object.keys(dbData).forEach(key => 
        (dbData[key] === undefined || dbData[key] === null) && delete dbData[key]
      );

      const method = profileData.id ? 'PUT' : 'POST';
      const url = profileData.id
        ? `${API_BASE_URL}/api/company-profile/${profileData.id}`
        : `${API_BASE_URL}/api/company-profile`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(dbData),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        console.warn('Company sync failed:', result.error || response.status);
        dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'error' });
        syncInProgress.current = false;
        return false;
      }

      const result = await response.json();
      if (result.success) {
        // Server may return data or just a success message — handle both
        const d = result.data || {};
        const updatedProfile = { ...profileData };
        if (d.id)                  updatedProfile.id                 = d.id.toString();
        if (d.company_name)        updatedProfile.name               = d.company_name;
        if (d.email)               updatedProfile.email              = d.email;
        if (d.industry)            updatedProfile.industry           = d.industry;
        if (d.employee_count)      updatedProfile.companySize        = d.employee_count.toString();
        if (d.phone)               updatedProfile.phone              = d.phone;
        if (d.website)             updatedProfile.website            = d.website;
        if (d.logo)                updatedProfile.logo               = d.logo;
        if (d.address)             updatedProfile.headquarters       = d.address;
        if (d.description)         updatedProfile.description        = d.description;
        if (d.vat_number)          updatedProfile.vatNumber          = d.vat_number;
        if (d.registration_number) updatedProfile.registrationNumber = d.registration_number;
        if (d.company_code)        updatedProfile.companyCode        = d.company_code;

        await AsyncStorage.setItem('company_profile', JSON.stringify(updatedProfile));
        dispatch({ type: actionTypes.SET_COMPANY_PROFILE, payload: updatedProfile });
        console.log('[Context] Company synced:', updatedProfile.name);
      }

      dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'idle' });
      syncInProgress.current = false;
      return true;
    } catch (error) {
      console.warn('Company sync error:', error?.message || error);
      dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'error' });
      syncInProgress.current = false;
      return false;
    }
  }, [state.apiAvailable]);

  const syncTeamToAPI = useCallback(async (members) => {
    if (!ENABLE_API_SYNC) return;
    if (!state.apiAvailable || members.length === 0) return;

    try {
      const token = await AsyncStorage.getItem('auth_token');
      const businessId = await getBusinessId();

      await Promise.all(members.map(member =>
        fetch(`${API_BASE_URL}/api/company/${businessId}/team`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ 
            ...member, 
            company_id: businessId,
            updated_at: new Date().toISOString() 
          }),
        }).catch(() => {})
      ));
    } catch (error) {
      console.error('Team sync error:', error);
    }
  }, [state.apiAvailable]);

  // ==================== USER ACTIONS ====================
  const saveUser = useCallback(async (userData) => {
    try {
      await AsyncStorage.setItem('user_data', JSON.stringify(userData));
      dispatch({ type: actionTypes.SET_USER, payload: userData });

      if (!state.user?.id) {
        addNotification({
          title: 'Welcome to JobEng!',
          message: `Hello ${userData.full_name || 'User'}, your account is ready.`,
          type: 'welcome',
          priority: 'high',
        });
      }

      return true;
    } catch (error) {
      console.error('Error saving user:', error);
      return false;
    }
  }, [state.user, addNotification]);

  const updateUser = useCallback(async (updates) => {
    const updatedUser = { ...state.user, ...updates };
    try {
      await AsyncStorage.setItem('user_data', JSON.stringify(updatedUser));
      dispatch({ type: actionTypes.UPDATE_USER, payload: updates });
      return true;
    } catch (error) {
      console.error('Error updating user:', error);
      return false;
    }
  }, [state.user]);

  // ==================== COMPANY PROFILE ACTIONS ====================
  const saveCompanyProfile = useCallback(async (profileData) => {
    try {
      await AsyncStorage.setItem('company_profile', JSON.stringify(profileData));
      dispatch({ type: actionTypes.SET_COMPANY_PROFILE, payload: profileData });

      if (ENABLE_API_SYNC && state.apiAvailable) {
        syncCompanyToAPI(profileData).catch(() => {});
      }

      return true;
    } catch (error) {
      console.error('Error saving company profile:', error);
      return false;
    }
  }, [state.apiAvailable, syncCompanyToAPI]);

  const updateCompanyProfile = useCallback(async (updates) => {
    const updatedProfile = { ...state.companyProfile, ...updates };
    try {
      await AsyncStorage.setItem('company_profile', JSON.stringify(updatedProfile));
      dispatch({ type: actionTypes.UPDATE_COMPANY_PROFILE, payload: updates });

      if (ENABLE_API_SYNC && state.apiAvailable) {
        syncCompanyToAPI(updatedProfile).catch(() => {});
      }

      return true;
    } catch (error) {
      console.error('Error updating company profile:', error);
      return false;
    }
  }, [state.companyProfile, state.apiAvailable, syncCompanyToAPI]);

  // ==================== TEAM MEMBER ACTIONS ====================
  const saveTeamMembers = useCallback(async (members) => {
    try {
      await AsyncStorage.setItem('team_members', JSON.stringify(members));
      dispatch({ type: actionTypes.SET_TEAM_MEMBERS, payload: members });

      if (ENABLE_API_SYNC && state.apiAvailable) {
        syncTeamToAPI(members).catch(() => {});
      }

      return true;
    } catch (error) {
      console.error('Error saving team members:', error);
      return false;
    }
  }, [state.apiAvailable, syncTeamToAPI]);

  const addTeamMember = useCallback(async (member) => {
    const newMember = {
      id: Date.now().toString(),
      status: 'online',
      createdAt: new Date().toISOString(),
      ...member,
    };

    const updated = [...state.teamMembers, newMember];
    await saveTeamMembers(updated);

    addNotification({
      title: 'Team Member Added',
      message: `${newMember.name} has been added to your team.`,
      type: 'info',
      priority: 'medium',
    });

    return newMember;
  }, [state.teamMembers, saveTeamMembers, addNotification]);

  const updateTeamMember = useCallback(async (memberId, updates) => {
    const updated = state.teamMembers.map(m =>
      m.id === memberId ? { ...m, ...updates } : m
    );
    await saveTeamMembers(updated);
  }, [state.teamMembers, saveTeamMembers]);

  const removeTeamMember = useCallback(async (memberId) => {
    const member = state.teamMembers.find(m => m.id === memberId);
    const updated = state.teamMembers.filter(m => m.id !== memberId);
    await saveTeamMembers(updated);

    addNotification({
      title: 'Team Member Removed',
      message: `${member?.name || 'A team member'} has been removed.`,
      type: 'warning',
      priority: 'medium',
    });
  }, [state.teamMembers, saveTeamMembers, addNotification]);

  // ==================== LOCATION ACTIONS ====================
  const addLocation = useCallback(async (location) => {
    const newLocation = {
      id: Date.now().toString(),
      ...location,
      createdAt: new Date().toISOString(),
    };

    const updated = [...state.locations, newLocation];
    dispatch({ type: actionTypes.SET_LOCATIONS, payload: updated });

    const updatedProfile = { ...state.companyProfile, locations: updated };
    await saveCompanyProfile(updatedProfile);

    addNotification({
      title: 'Location Added',
      message: `${location.name} has been added.`,
      type: 'info',
      priority: 'low',
    });
  }, [state.locations, state.companyProfile, saveCompanyProfile, addNotification]);

  const removeLocation = useCallback(async (index) => {
    const location = state.locations[index];
    const updated = state.locations.filter((_, i) => i !== index);
    dispatch({ type: actionTypes.SET_LOCATIONS, payload: updated });

    const updatedProfile = { ...state.companyProfile, locations: updated };
    await saveCompanyProfile(updatedProfile);

    addNotification({
      title: 'Location Removed',
      message: `${location?.name || 'Location'} has been removed.`,
      type: 'warning',
      priority: 'low',
    });
  }, [state.locations, state.companyProfile, saveCompanyProfile, addNotification]);

  // ==================== AWARD ACTIONS ====================
  const addAward = useCallback(async (award) => {
    const newAward = {
      id: Date.now().toString(),
      ...award,
      createdAt: new Date().toISOString(),
    };

    const updated = [...state.awards, newAward];
    dispatch({ type: actionTypes.SET_AWARDS, payload: updated });

    const updatedProfile = { ...state.companyProfile, awards: updated };
    await saveCompanyProfile(updatedProfile);

    addNotification({
      title: 'Award Added',
      message: `${award.title} has been added.`,
      type: 'info',
      priority: 'low',
    });
  }, [state.awards, state.companyProfile, saveCompanyProfile, addNotification]);

  const removeAward = useCallback(async (index) => {
    const award = state.awards[index];
    const updated = state.awards.filter((_, i) => i !== index);
    dispatch({ type: actionTypes.SET_AWARDS, payload: updated });

    const updatedProfile = { ...state.companyProfile, awards: updated };
    await saveCompanyProfile(updatedProfile);

    addNotification({
      title: 'Award Removed',
      message: `${award?.title || 'Award'} has been removed.`,
      type: 'warning',
      priority: 'low',
    });
  }, [state.awards, state.companyProfile, saveCompanyProfile, addNotification]);

  // ==================== BUSINESS STATS ACTIONS ====================
  const updateBusinessStats = useCallback(async (stats) => {
    try {
      await AsyncStorage.setItem('business_stats', JSON.stringify(stats));
      dispatch({ type: actionTypes.SET_BUSINESS_STATS, payload: stats });
    } catch (error) {
      console.error('Error updating business stats:', error);
    }
  }, []);

  // ==================== SUBSCRIPTION ACTIONS ====================
  const saveSubscription = useCallback(async (subscriptionData) => {
    try {
      // Normalise before saving — prevents stale £599 from overwriting correct £89
      const rawPrice = parseFloat(subscriptionData.price || subscriptionData.price_paid || 0);
      const normSub = {
        ...subscriptionData,
        price:     rawPrice,
        plan_name: subscriptionData.plan_name || subscriptionData.name || subscriptionData.plan_id || 'Business Plan',
        // Ensure status is correct — if price > 0 and not explicitly cancelled/expired, it's active
        status:    subscriptionData.status && subscriptionData.status !== 'inactive'
                   ? subscriptionData.status
                   : rawPrice > 0 ? 'active' : 'inactive',
      };
      // Save locally first
      await AsyncStorage.setItem('business_subscription', JSON.stringify(normSub));
      dispatch({ type: actionTypes.SET_SUBSCRIPTION, payload: normSub });

      // Sync to server if enabled and online
      if (ENABLE_API_SYNC && state.apiAvailable) {
        const token = await AsyncStorage.getItem('auth_token');
        const businessId = await getBusinessId();
        
        if (!token || !businessId) {
          console.warn('Missing token or businessId, subscription not synced');
          return true;
        }

        // ── INTEGRITY CHECK ─────────────────────────────────────────────────
        // Reject sync if data looks corrupt:
        // 1. plan_id doesn't match a known business plan
        // 2. price is 0 or negative
        // 3. plan_id and plan_name are inconsistent (stale cache mix)
        const VALID_PLAN_IDS = [
          'free_trial','single_starter','single_professional','single_enterprise',
          'team_starter','team_professional','team_enterprise',
          'multi_team','multi_business','multi_enterprise',
          'pro_basic','pro_plus','learn_basic','learn_pro',
        ];
        const planId   = subscriptionData.plan_id;
        const planName = subscriptionData.plan_name || '';
        const price    = parseFloat(subscriptionData.price || 0);

        // Skip sync if plan_id is not in known list
        if (planId && !VALID_PLAN_IDS.includes(planId)) {
          console.warn(`[Context] Skipping sync — unknown plan_id: ${planId}`);
          return true;
        }

        // // If price is 0 for a paid plan, fetch real price from server before syncing
        // if (price === 0 && planId && planId !== 'free_trial') {
        // try {
        // const planRes = await fetch(`${API_BASE_URL}/api/business-subscriptions?company_id=${businessId}&status=active`, {
        // headers: { 'X-API-Key': API_KEY, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        // });
        // const planJson = await planRes.json().catch(() => ({}));
        // if (planJson.success && planJson.data && planJson.data[0] && (planJson.data[0].price_paid || planJson.data[0].price)) {
        // subscriptionData.price = parseFloat((planJson.data[0].price_paid || planJson.data[0].price));
        // subscriptionData.price_paid = subscriptionData.price;
        // console.log('[Context] Fetched real price from server:', subscriptionData.price);
        // } else {
        // console.warn('[Context] Could not fetch plan price, skipping sync');
        // return true;
        // }
        // } catch (e) {
        // console.warn('[Context] Price fetch failed, skipping sync', e.message, e);
        // return true;
        // }
        // }

        // Skip sync if plan_id and plan_name are clearly mismatched (old stale cache)
        // e.g. plan_id='multi_enterprise' but plan_name='Business Professional'
        const nameIncludesId = planId && planName && (
          planName.toLowerCase().includes(planId.replace(/_/g,' ').split('_')[0]) ||
          planId.includes('professional') === planName.toLowerCase().includes('professional') ||
          planId.includes('enterprise')   === planName.toLowerCase().includes('enterprise') ||
          planId.includes('starter')      === planName.toLowerCase().includes('starter')
        );
        if (planId && planName && !nameIncludesId) {
          console.warn(`[Context] Skipping sync — plan_id/plan_name mismatch: ${planId} vs "${planName}" — fetching fresh from DB instead`);
          // Don't sync — the screen's fetchSubscription will correct the DB next load
          return true;
        }
        // ────────────────────────────────────────────────────────────────────

        // Prepare clean subscription data for server
          const cleanData = {
          billing_cycle: subscriptionData.billing_cycle,
          company_id:    businessId,
          status:        subscriptionData.status || 'active',
          start_date:    subscriptionData.start_date || new Date().toISOString(),
          };











        // Remove undefined values
        Object.keys(cleanData).forEach(key => 
          cleanData[key] === undefined && delete cleanData[key]
        );

        // First check if subscription exists
        const checkResponse = await fetch(
          `${API_BASE_URL}/api/business-subscriptions?company_id=${businessId}&status=active`,
          {
            headers: {
              'X-API-Key': API_KEY,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        
        if (!checkResponse.ok) {
          console.warn('Failed to check subscription status', checkResponse.status);
          return true; // Still return true since local save succeeded
        }
        
        const checkResult = await checkResponse.json();
        const existingSubscription = checkResult.success && checkResult.data && checkResult.data.length > 0 
          ? checkResult.data[0] 
          : null;

        // ── GUARD: never write onto another company's subscription ─────────
        // Even if resolution were somehow stale, refuse to PUT a record whose
        // owner doesn't match the active company. Prevents the cross-company
        // write (e.g. a company-9 session editing company 1's #35).
        if (existingSubscription) {
          const ownerId = parseInt(
            existingSubscription.business_id ?? existingSubscription.company_id, 10
          );
          if (ownerId && ownerId !== businessId) {
            console.warn(
              `[Context] ABORT sync — subscription #${existingSubscription.id} ` +
              `belongs to company ${ownerId}, not active company ${businessId}`
            );
            return true;
          }
        }

        const method = existingSubscription ? 'PUT' : 'POST';
        const url = existingSubscription
          ? `${API_BASE_URL}/api/business-subscriptions/${existingSubscription.id}`
          : `${API_BASE_URL}/api/business-subscriptions`;

        console.log('🔄 Syncing subscription:', { url, method, data: cleanData });

        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(cleanData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.warn('Failed to sync subscription to server', response.status, errorText);
        } else {
          const result = await response.json();
          if (result.success && result.data) {
            // Update local state with server response
            const serverSub = {
              ...result.data,
              price:     parseFloat(result.data.price || result.data.price_paid || 0),
              plan_name: result.data.plan_name || result.data.name || result.data.plan_id || 'Business Plan',
              company_id: businessId,
            };
            await AsyncStorage.setItem('business_subscription', JSON.stringify(serverSub));
            dispatch({ type: actionTypes.SET_SUBSCRIPTION, payload: serverSub });
            console.log('✅ Subscription synced successfully');
          }
        }
      }
      return true;
    } catch (error) {
      console.error('Error saving subscription:', error);
      // Don't throw, just return false since local save succeeded
      return false;
    }
  }, [state.apiAvailable]);

  const updateSubscription = useCallback(async (updates) => {
    const updated = { ...state.subscription, ...updates };
    await saveSubscription(updated);
  }, [state.subscription, saveSubscription]);

  // ==================== SUBSCRIPTION FEATURE CHECK ====================
  const isFeatureAvailable = useCallback((requiredPlan) => {
    const sub = state.subscription;
    if (!sub) return false;

    // Accept any non-inactive, non-expired status
    const activeStatuses = ['active', 'trial', 'paid'];
    const price    = parseFloat(sub.price || sub.price_paid || 0);
    const planId   = sub.plan_id || '';
    const isFree   = planId === 'free_trial' || price === 0;
    const isActive = activeStatuses.includes(sub.status) || (!isFree && price > 0);

    if (!isActive) return false;
    if (!requiredPlan) return true; // no specific plan required

    const planHierarchy = {
      'free_trial':          0,
      'learn_basic':         1,
      'pro_basic':           1,
      'learn_pro':           2,
      'pro_plus':            2,
      'single_starter':      3,
      'single_professional': 4,
      'team_starter':        4,
      'single_enterprise':   5,
      'multi_team':          4,
      'team_professional':   5,
      'multi_business':      5,
      'multi_enterprise':    6,
      'team_enterprise':     6,
    };

    const requiredPlanMap = {
      'free':         0,
      'basic':        1,
      'starter':      3,
      'professional': 4,
      'team':         4,
      'enterprise':   5,
      'business':     4,
    };

    const userLevel     = planHierarchy[planId] ?? 0;
    const requiredLevel = requiredPlanMap[requiredPlan] ?? 0;
    return userLevel >= requiredLevel;
  }, [state.subscription]);

  const getSubscriptionStatus = useCallback(() => {
    const sub = state.subscription;
    const price  = parseFloat(sub?.price || sub?.price_paid || 0);
    const planId = sub?.plan_id || '';
    const activeStatuses = ['active', 'trial', 'paid'];
    const isActive = sub && (activeStatuses.includes(sub.status) || (planId !== 'free_trial' && price > 0));

    if (!isActive) {
      return {
        status: 'No Active Subscription',
        color: '#E91E63',
        icon: 'alert-circle',
        message: 'Subscribe to unlock all features',
      };
    }

    const planType = state.subscription?.plan_type || '';
    const planName = state.subscription?.plan_name || '';

    return {
      status: planType === 'single_user' ? 'Single User Plan' : 'Multi-User Plan',
      color: '#4CAF50',
      icon: 'checkmark-circle',
      message: planName ? `${planName} Active` : 'Plan Active',
    };
  }, [state.subscription]);

  // ==================== SYNC ALL DATA ====================
  const syncAllData = useCallback(async () => {
    if (!ENABLE_API_SYNC) {
      Alert.alert('Info', 'Syncing is currently disabled.');
      return;
    }
    if (!state.apiAvailable) {
      Alert.alert('Offline', 'You are offline. Please connect to the internet to sync.');
      return;
    }

    dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'syncing' });

    try {
      await Promise.allSettled([
        syncCompanyToAPI(state.companyProfile),
        syncTeamToAPI(state.teamMembers),
      ]);

      dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'idle' });

      addNotification({
        title: 'Sync Complete',
        message: 'All data has been synced successfully.',
        type: 'success',
        priority: 'low',
      });

    } catch (error) {
      console.error('Sync all error:', error);
      dispatch({ type: actionTypes.SET_SYNC_STATUS, payload: 'error' });

      addNotification({
        title: 'Sync Failed',
        message: 'Failed to sync data. Please try again.',
        type: 'error',
        priority: 'high',
      });
    }
  }, [state.apiAvailable, state.companyProfile, state.teamMembers, syncCompanyToAPI, syncTeamToAPI, addNotification]);

  // ==================== REFRESH ====================
  const refreshData = useCallback(async () => {
    dispatch({ type: actionTypes.SET_REFRESHING, payload: true });
    await loadAllData();
    dispatch({ type: actionTypes.SET_REFRESHING, payload: false });
  }, [loadAllData]);

  // ==================== CLEAR ALL DATA (LOGOUT) ====================
  const clearAllData = useCallback(async () => {
    try {
      const keys = [
        'user_data',
        'company_profile',
        'business_subscription',
        'team_members',
        'business_stats',
        'notifications',
        'auth_token',
        'refresh_token',
        'business_data_backup',
        'user_id',
        'user_email',
        'user_name',
        'company_id',
      ];

      await AsyncStorage.multiRemove(keys);
      dispatch({ type: actionTypes.CLEAR_ALL_DATA });

      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  }, []);

  // ==================== UI ACTIONS ====================
  const setActiveTab = (tab) => {
    dispatch({ type: actionTypes.SET_ACTIVE_TAB, payload: tab });
  };

  const setShowNotifications = (show) => {
    dispatch({ type: actionTypes.SET_SHOW_NOTIFICATIONS, payload: show });
  };

  const setShowQuickActions = (show) => {
    dispatch({ type: actionTypes.SET_SHOW_QUICK_ACTIONS, payload: show });
  };

  const clearError = () => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  };

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    loadAllData();
    checkConnectivity();

    const interval = setInterval(checkConnectivity, 30000);
    return () => clearInterval(interval);
  }, [loadAllData, checkConnectivity]);

  // Sync company profile when API becomes available (covers login + app resume)
  useEffect(() => {
    if (state.apiAvailable) {
      fetchAndSyncCompanyProfile();
    }
  }, [state.apiAvailable, fetchAndSyncCompanyProfile]);

  // Also sync when user ID changes (explicit login event)
  useEffect(() => {
    const syncOnUserChange = async () => {
      if (!state.user?.id) return;
      // Delay slightly to allow auth handler to store company_id first
      await new Promise(r => setTimeout(r, 800));
      await fetchAndSyncCompanyProfile();
    };
    syncOnUserChange();
  }, [state.user?.id, fetchAndSyncCompanyProfile]);

  // Save data on changes
  useEffect(() => {
    saveAllData();
  }, [saveAllData, state.user, state.companyProfile, state.subscription, state.teamMembers, state.notifications]);

  // Context value
  const value = {
    // State
    ...state,

    // Connectivity
    checkConnectivity,

    // User Actions
    saveUser,
    updateUser,

    // Company Profile Actions
    saveCompanyProfile,
    updateCompanyProfile,
    fetchAndSyncCompanyProfile,

    // Team Member Actions
    saveTeamMembers,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,

    // Location Actions
    addLocation,
    removeLocation,

    // Award Actions
    addAward,
    removeAward,

    // Business Stats Actions
    updateBusinessStats,

    // Subscription Actions
    saveSubscription,
    updateSubscription,
    isFeatureAvailable,
    getSubscriptionStatus,

    // Notification Actions
    addNotification,
    markNotificationRead,
    clearNotifications,

    // Sync Actions
    syncAllData,
    ensureUserHasCompany,

    // Refresh Actions
    refreshData,
    reloadData: loadAllData,

    // Clear All (Logout)
    clearAllData,

    // UI Actions
    setActiveTab,
    setShowNotifications,
    setShowQuickActions,
    clearError,

    // Direct setter for notifications
    setNotifications: (notifications) => dispatch({ type: actionTypes.SET_NOTIFICATIONS, payload: notifications }),
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

// Custom hook to use business context
export const useBusiness = () => {
  const ctx = useContext(BusinessContext);
  if (!ctx) {
    throw new Error('useBusiness must be used within a <BusinessProvider />');
  }
  return ctx;
};

export default BusinessContext;
